import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { download } from "@/lib/download";
import { formatNumber } from "@/lib/format";
import ImportBarcodeGen from "@/components/products/ImportBarcodeGen";

export default function CodeGenDialog({ onClose, initialMode = "new", poId = "" }) {
  const [mode, setMode] = useState(poId ? "import" : initialMode);
  const [st, setSt] = useState(null);
  const [n, setN] = useState(10);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [idem] = useState(() => crypto.randomUUID());
  useEffect(() => { api.get("/identifiers/gs1").then(({ data }) => setSt(data)).catch((e) => toast.error(apiError(e))); }, []);
  const max = st ? Math.min(st.available_codes, st.available_gtin || 0) : 0;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/identifiers/batches", { count: Number(n) }, { headers: { "Idempotency-Key": idem } });
      setDone(data);
      toast.success(`${data.batch_no}: ${data.count} kod–barkod çifti rezerve edildi`);
      download(`/identifiers/batches/${data.id}/excel`, {}, `${data.batch_no}.xlsx`).catch(() => toast.error("Excel indirilemedi; rezervasyon kayıtlı, Kod Üretim Geçmişi'nden tekrar indirin"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const over = Number(n) > max;
  const tab = (m, t) => <button type="button" onClick={() => setMode(m)} className={`rounded px-3 py-1 text-xs ${mode === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`codegen-mode-${m}`}>{t}</button>;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className={mode === "import" ? "max-w-5xl sm:max-w-5xl" : ""} data-testid="codegen-dialog">
        <DialogHeader><DialogTitle>{mode === "import" ? "İthalat Siparişinden Barkod Üret" : "Kod Üret (4 haneli kod + GS1 GTIN-13)"}</DialogTitle>
          <DialogDescription>{mode === "import" ? "Ürün kodu olup barkodu olmayan sipariş satırlarına merkezi kayıt defterinden GS1 GTIN-13." : "Yeni kod–barkod çiftleri rezerve edilir."}</DialogDescription></DialogHeader>
        {!poId && <div className="flex gap-2">{tab("new", "Yeni Kod + Barkod")}{tab("import", "İthalat Siparişinden Barkod Üret")}</div>}
        {mode === "import" ? <ImportBarcodeGen poId={poId} /> : !st ? <div className="h-20 animate-pulse" /> : (
          <div className="space-y-3 text-sm">
            {st.blocked && <div className="rounded border border-rose-300 bg-rose-50 p-2 text-rose-700" data-testid="codegen-blocked">{st.blocked}</div>}
            <div className="grid grid-cols-2 gap-2">
              <div className="panel p-2"><div className="text-[11px] text-slate-500">Boş 4 haneli kod (0000–9999)</div><b className="font-mono" data-testid="codegen-avail-codes">{formatNumber(st.available_codes)}</b></div>
              <div className="panel p-2"><div className="text-[11px] text-slate-500">Boş GTIN (önek {st.prefix || "—"})</div><b className="font-mono" data-testid="codegen-avail-gtin">{formatNumber(st.available_gtin || 0)}</b></div>
            </div>
            <label className="block">Kaç adet kod–barkod çifti oluşturulsun?
              <input type="number" min={1} max={2000} value={n} onChange={(e) => setN(e.target.value)} className="mt-1 h-9 w-full rounded border px-2" data-testid="codegen-count" /></label>
            {over && <div className="text-rose-600" data-testid="codegen-over">En fazla {formatNumber(max)} çift oluşturulabilir. 5 haneye geçilmez.</div>}
            {done && <div className="rounded bg-emerald-50 p-2 text-emerald-800" data-testid="codegen-done">{done.batch_no} kaydedildi · {done.count} çift · Excel indiriliyor</div>}
          </div>)}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Kapat</Button>
          {mode === "new" && <Button disabled={!st || !!st.blocked || over || busy || !!done || Number(n) < 1} onClick={run} data-testid="codegen-submit">{busy ? "Rezerve ediliyor…" : "Oluştur ve Rezerve Et"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
